import { useEffect, useState } from 'react';
import { Col, Form, Modal, Spinner } from 'react-bootstrap';
import InvoiceAttachmentType, {
    InvoicePrintAttachmentTypeTranslate,
} from '../../../constants/InvoicePrintAttachmentType';
import { InvoiceStatus } from '../../../constants/invoiceStatus';
import InvoiceHttpService from '../../../services/http/invoice-http';
import handleResponseError from '../../../utils/handleResponseError';
import { StyledConfirmButton } from './styles';
import StaticDocumentsHttpService from '../../../services/http/statics-documents-http';
import StaticDocuments from '../../../constants/static-documents';

interface InvoicePrintModalProps {
    show: boolean;
    invoice: any;
    onHide: () => void;
}

export default function InvoicePrintModal({
    show,
    invoice,
    onHide,
}: InvoicePrintModalProps) {
    const [selectedTypes, setSelectedTypes] = useState<InvoiceAttachmentType[]>(
        [],
    );
    const [insuranceDocument, setInsuranceDocument] = useState<boolean>(false);
    const [loading, setLoading] = useState<
        'GET' | 'VIEW' | 'DOWNLOAD' | undefined
    >(undefined);

    const [checkboxOptions, setCheckboxOptions] = useState<
        Array<{
            type: InvoiceAttachmentType;
            label: InvoicePrintAttachmentTypeTranslate;
            visible: boolean;
        }>
    >([]);

    function handleClosed() {
        setCheckboxOptions([]);
        setSelectedTypes([]);
        onHide();
    }

    function handleSelectedTypes(
        checked: boolean,
        type: InvoiceAttachmentType,
    ) {
        if (checked) return setSelectedTypes([...selectedTypes, type]);

        return setSelectedTypes(
            selectedTypes.filter((current) => current !== type),
        );
    }

    async function downloadInsuranceAttachment(type: StaticDocuments) {
        try {
            await StaticDocumentsHttpService.downloadCurrent(type);
        } catch (error: any) {
            handleResponseError(
                error,
                'Erro ao encontrar instruções.',
            );
        }
    }

    async function downloadAttachmentByType(type: InvoiceAttachmentType) {
        try {
            if (type === InvoiceAttachmentType.InvoicePdf) {
                await InvoiceHttpService.print(invoice.id, 'order', true);
            } else {
                await InvoiceHttpService.downloadAttachmentByType(
                    invoice.id,
                    type,
                );
            }
        } catch (error: any) {
            handleResponseError(
                error,
                `Erro ao encontrar ${InvoicePrintAttachmentTypeTranslate[type]}`,
            );
        }
    }
    
    async function viewAttachmentByType(type: InvoiceAttachmentType) {
        try {
            const response = await (type === InvoiceAttachmentType.InvoicePdf
                ? InvoiceHttpService.print(invoice.id, 'order', false)
                : InvoiceHttpService.viewAttachmentByType(invoice.id, type));
            if (!response) throw new Error('');
            const file = new Blob([response.data], {
                type: 'application/pdf',
            });
            const fileURL = URL.createObjectURL(file);

            window.open(fileURL, '_blank');
        } catch (error: any) {
            handleResponseError(
                error,
                `Erro ao encontrar documento.`,
            );
        }
    }

    const viewInsuranceAttachment = async (type: StaticDocuments) => {
        try {
            const file: File = await StaticDocumentsHttpService.getCurrent(type);
            
            const url = window.URL.createObjectURL(file);

            window.open(url, "_blank");
            
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (error: any) {
            console.error(error);
            handleResponseError(
                error,
                `Erro ao encontrar documento de axiliar do seguro.`,
            );
        }
    }

    const handleView = async () => {
        if (loading === 'VIEW') return;
        setLoading('VIEW');

        if (insuranceDocument)
            await viewInsuranceAttachment(StaticDocuments.EngineeringInsuranceInstructions);
        

        await Promise.allSettled(
            selectedTypes.map((type) => viewAttachmentByType(type)),
        );


        setLoading(undefined);
    };

    const handleDownload = async () => {
        if (loading === 'DOWNLOAD') return;
        setLoading('DOWNLOAD');

         if (insuranceDocument)
            await downloadInsuranceAttachment(StaticDocuments.EngineeringInsuranceInstructions);

        await Promise.allSettled(
            selectedTypes.map((type) => downloadAttachmentByType(type)),
        );

        setLoading(undefined);
    };

    const showEngineeringInsurance = invoice?.engineeringInsuranceValue > 0 && invoice?.engineeringInsuranceIsActive;

    async function handleCheckboxOptions() {
        if (loading === 'GET') return;

        try {
            setLoading('GET');

            const { data: canGetBankSlipM8 } =
                await InvoiceHttpService.validateCanGetBankSlipM8(invoice.id);

            const options = [
                {
                    type: InvoiceAttachmentType.InvoicePdf,
                    label: InvoicePrintAttachmentTypeTranslate.INVOICE_PDF,
                    visible: true,
                },
                {
                    type: InvoiceAttachmentType.Nf,
                    label: InvoicePrintAttachmentTypeTranslate.NF,
                    visible:
                        invoice &&
                        !invoice.futureSale &&
                        [
                            InvoiceStatus.InvoiceBilled,
                            InvoiceStatus.InvoiceDispatched,
                            InvoiceStatus.WaitingDispatch,
                        ].includes(invoice.status),
                },
                {
                    type: InvoiceAttachmentType.NfFuture,
                    label: InvoicePrintAttachmentTypeTranslate.NF_FUTURE,
                    visible: invoice && !!invoice.futureSale,
                },
                {
                    type: InvoiceAttachmentType.NfSimpleShipping,
                    label: InvoicePrintAttachmentTypeTranslate.NF_SIMPLE_SHIPPING,
                    visible:
                        invoice &&
                        !!invoice.futureSale &&
                        [
                            InvoiceStatus.InvoiceBilled,
                            InvoiceStatus.InvoiceDispatched,
                            InvoiceStatus.WaitingDispatch,
                        ].includes(invoice.status),
                },
                {
                    type: InvoiceAttachmentType.BankSlip,
                    label: InvoicePrintAttachmentTypeTranslate.BANK_SLIP,
                    visible: !!canGetBankSlipM8,
                },
            ];

            setCheckboxOptions(options);
        } catch (e) {
            handleResponseError(e, 'Erro ao carregar opções');
        } finally {
            setLoading(undefined);
        }
    }

    useEffect(() => {
        if (invoice?.id && show) {
            handleCheckboxOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice?.id, show]);

    return (
        <Modal show={show} onHide={() => handleClosed()} centered>
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Impressão
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {(
                    loading === 'GET' ? (
                        <Col md={12} className="text-center">
                            <Spinner animation="border" />
                        </Col>
                    ) : (
                        checkboxOptions
                            .filter((option) => option.visible)
                            .map(({ type, label }) => (
                                <Form.Check
                                    key={type}
                                    type="checkbox"
                                    label={label}
                                    checked={selectedTypes.includes(type)}
                                    onChange={(e) =>
                                        handleSelectedTypes(e.target.checked, type)
                                    }
                                    className="mb-3"
                                />
                            ))
                    )
                )}
                {
                    showEngineeringInsurance &&
                    (
                        <Form.Check
                            type="checkbox"
                            label="Instruções risco engenharia"
                            checked={insuranceDocument}
                            onChange={(e) =>
                                setInsuranceDocument(e.target.checked)
                            }
                            className="mb-3"
                        />
                    )
                }
            </Modal.Body>

            <Modal.Footer>
                <StyledConfirmButton
                    onClick={handleView}
                    disabled={loading || (selectedTypes.length === 0 && !insuranceDocument)}
                    size="medium"
                >
                    {loading === 'VIEW' ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        'Visualizar'
                    )}
                </StyledConfirmButton>
                <StyledConfirmButton
                    onClick={handleDownload}
                    disabled={loading || (selectedTypes.length === 0 && !insuranceDocument)}
                >
                    {loading === 'DOWNLOAD' ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        'Download'
                    )}
                </StyledConfirmButton>
            </Modal.Footer>
        </Modal>
    );
}
